import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ProtectedRoute } from '@/components/layout';
import ActivityTracker from '@/components/ActivityTracker';
import './App.css';

// Every routed page is lazy-loaded (route-based code splitting): public visitors
// only download the public-page chunks; the admin/delegate/mentor bundles load
// after login. Keeps the initial JS payload small for SEO/Core Web Vitals.

// Public Pages
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const ContactPage = lazy(() => import('@/pages/public/ContactPage'));
const MentorsPage = lazy(() => import('@/pages/public/MentorsPage'));
const PublicCoursesPage = lazy(() => import('@/pages/public/PublicCoursesPage'));
const VerifyCertificatePage = lazy(() => import('@/pages/public/VerifyCertificatePage'));
const PublicBlogsPage = lazy(() => import('@/pages/public/PublicBlogsPage'));
const SingleBlogPage = lazy(() => import('@/pages/public/SingleBlogPage'));
const GalleryPage = lazy(() => import('@/pages/public/GalleryPage'));
const EventsPage = lazy(() => import('@/pages/public/EventsPage'));
const EventDetailPage = lazy(() => import('@/pages/public/EventDetailPage'));

// Auth Pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const AuthCallback = lazy(() => import('@/pages/auth/AuthCallback'));
const SSOCallback = lazy(() => import('@/pages/auth/SSOCallback'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const SetPasswordPage = lazy(() => import('@/pages/auth/SetPasswordPage'));

// Programme Pages
const ProgrammePage = lazy(() => import('@/pages/programmes/ProgrammePage'));
const PracticeFreedomRetreatPage = lazy(() => import('@/pages/programmes/practice-freedom-retreat/PracticeFreedomRetreatPage'));

// Delegate Pages
const DelegateDashboard = lazy(() => import('@/pages/delegate/DelegateDashboard'));
const CoursesPage = lazy(() => import('@/pages/delegate/CoursesPage'));
const CourseMaterialsPage = lazy(() => import('@/pages/delegate/CourseMaterialsPage'));
const CourseChatPage = lazy(() => import('@/pages/delegate/CourseChatPage'));
const AssignmentsPage = lazy(() => import('@/pages/delegate/AssignmentsPage'));
const ResourcesPage = lazy(() => import('@/pages/delegate/ResourcesPage'));
const CertificatesPage = lazy(() => import('@/pages/delegate/CertificatesPage'));
const DiscussionsPage = lazy(() => import('@/pages/delegate/DiscussionsPage'));
const PaymentsPage = lazy(() => import('@/pages/delegate/PaymentsPage'));
const MyDocumentsPage = lazy(() => import('@/pages/delegate/MyDocumentsPage'));
const MyCourseworkPage = lazy(() => import('@/pages/delegate/MyCourseworkPage'));
const NotificationsPage = lazy(() => import('@/pages/delegate/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/delegate/SettingsPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/delegate/PaymentSuccessPage'));

// Mentor Pages (MentorReviewPage.js default-exports MentorReviewPage; the list
// page is a named export, so remap it to a default for React.lazy)
const MentorDashboard = lazy(() => import('@/pages/mentor/MentorDashboard'));
const MentorReviewPage = lazy(() => import('@/pages/mentor/MentorReviewPage'));
const MentorReviewsListPage = lazy(() =>
  import('@/pages/mentor/MentorReviewPage').then((m) => ({ default: m.MentorReviewsListPage }))
);
const MentorDelegatesPage = lazy(() => import('@/pages/mentor/MentorDelegatesPage'));

// Admin Pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminUserProfilePage = lazy(() => import('@/pages/admin/AdminUserProfilePage'));
const AdminCoursesPage = lazy(() => import('@/pages/admin/AdminCoursesPage'));
const AdminCohortsPage = lazy(() => import('@/pages/admin/AdminCohortsPage'));
const AdminAssignmentsPage = lazy(() => import('@/pages/admin/AdminAssignmentsPage'));
const AdminAnnouncementsPage = lazy(() => import('@/pages/admin/AdminAnnouncementsPage'));
const AdminFeedbackPage = lazy(() => import('@/pages/admin/AdminFeedbackPage'));
const AdminPaymentsPage = lazy(() => import('@/pages/admin/AdminPaymentsPage'));
const AdminLeadsPage = lazy(() => import('@/pages/admin/AdminLeadsPage'));
const AdminTestimonialsPage = lazy(() => import('@/pages/admin/AdminTestimonialsPage'));
const AdminCourseMaterialsPage = lazy(() => import('@/pages/admin/AdminCourseMaterialsPage'));
const AdminWebsiteSettingsPage = lazy(() => import('@/pages/admin/AdminWebsiteSettingsPage'));
const AdminReferralsPage = lazy(() => import('@/pages/admin/AdminReferralsPage'));
const AdminEmailSequencesPage = lazy(() => import('@/pages/admin/AdminEmailSequencesPage'));
const AdminCourseSchedulePage = lazy(() => import('@/pages/admin/AdminCourseSchedulePage'));
const AdminDataExportPage = lazy(() => import('@/pages/admin/AdminDataExportPage'));
const AdminFilesPage = lazy(() => import('@/pages/admin/AdminFilesPage'));
const AdminSupportPage = lazy(() => import('@/pages/admin/AdminSupportPage'));
const AdminProgrammesPage = lazy(() => import('@/pages/admin/AdminProgrammesPage'));
const AdminEventsPage = lazy(() => import('@/pages/admin/AdminEventsPage'));
const AdminGalleryPage = lazy(() => import('@/pages/admin/AdminGalleryPage'));
const AdminWaitlistPage = lazy(() => import('@/pages/admin/AdminWaitlistPage'));
const AdminDatabaseMirroringPage = lazy(() => import('@/pages/admin/AdminDatabaseMirroringPage'));
const AdminStoragePage = lazy(() => import('@/pages/admin/AdminStoragePage'));
const AdminBlogsPage = lazy(() => import('@/pages/admin/AdminBlogsPage'));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/AdminAuditLogPage'));
const AdminUserActivityPage = lazy(() => import('@/pages/admin/AdminUserActivityPage'));
const AdminErrorsPage = lazy(() => import('@/pages/admin/AdminErrorsPage'));
const AdminEmailsPage = lazy(() => import('@/pages/admin/AdminEmailsPage'));

// Shown while a lazy route chunk loads (same idiom as ProtectedRoute's loader).
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
    <Loader2 className="w-6 h-6 animate-spin text-[#41EAD4]" />
  </div>
);

// Router component that handles session_id detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment for session_id (Emergent OAuth callback)
  // This must be done synchronously during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AuthCallback />
      </Suspense>
    );
  }

  return (
    <>
      <ActivityTracker />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
      {/* SSO Callback Routes - must be before protected routes */}
      <Route path="/auth/sso" element={<SSOCallback />} />
      <Route path="/sso-callback" element={<SSOCallback />} />
      
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/programmes" element={<PublicCoursesPage />} />
      {/* Hand-built enquiry page — not part of the programmes module */}
      <Route path="/programmes/practice-freedom-retreat" element={<PracticeFreedomRetreatPage />} />
      {/* Dynamic programme page for DB-driven content */}
      <Route path="/programmes/:slug" element={<ProgrammePage />} />
      <Route path="/mentors" element={<MentorsPage />} />
      <Route path="/blogs" element={<PublicBlogsPage />} />
      <Route path="/blogs/:slug" element={<SingleBlogPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/events/:slug" element={<EventDetailPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/verify/:certificateNumber" element={<VerifyCertificatePage />} />
      
      {/* Protected Delegate Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DelegateDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <CoursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId"
        element={
          <ProtectedRoute>
            <CoursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assignments"
        element={
          <ProtectedRoute>
            <AssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <ResourcesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/certificates"
        element={
          <ProtectedRoute>
            <CertificatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/materials"
        element={
          <ProtectedRoute>
            <CourseMaterialsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-coursework"
        element={
          <ProtectedRoute>
            <MyCourseworkPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/discussions"
        element={
          <ProtectedRoute>
            <DiscussionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-documents"
        element={
          <ProtectedRoute>
            <MyDocumentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/:courseId/chat"
        element={
          <ProtectedRoute>
            <CourseChatPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses"
        element={
          <ProtectedRoute adminOnly>
            <AdminCoursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/:courseId/materials"
        element={
          <ProtectedRoute adminOnly>
            <AdminCourseMaterialsPage />
          </ProtectedRoute>
        }
      />
      {/* Mentor access to course materials (read-only) */}
      <Route
        path="/mentor/courses/:courseId/materials"
        element={
          <ProtectedRoute mentorOnly>
            <AdminCourseMaterialsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cohorts"
        element={
          <ProtectedRoute adminOnly>
            <AdminCohortsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assignments"
        element={
          <ProtectedRoute adminOnly>
            <AdminAssignmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/access"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute adminOnly>
            <AdminAnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute adminOnly>
            <AdminFeedbackPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <ProtectedRoute adminOnly>
            <AdminPaymentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <ProtectedRoute adminOnly>
            <AdminLeadsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/testimonials"
        element={
          <ProtectedRoute adminOnly>
            <AdminTestimonialsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/website-settings"
        element={
          <ProtectedRoute siteContentOnly>
            <AdminWebsiteSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <ProtectedRoute adminOnly>
            <AdminReferralsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-sequences"
        element={
          <ProtectedRoute adminOnly>
            <AdminEmailSequencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/course-schedule"
        element={
          <ProtectedRoute adminOnly>
            <AdminCourseSchedulePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-export"
        element={
          <ProtectedRoute adminOnly>
            <AdminDataExportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/files"
        element={
          <ProtectedRoute adminOnly>
            <AdminFilesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute adminOnly>
            <AdminSupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/programmes"
        element={
          <ProtectedRoute adminOnly>
            <AdminProgrammesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/events"
        element={
          <ProtectedRoute siteContentOnly>
            <AdminEventsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gallery"
        element={
          <ProtectedRoute siteContentOnly>
            <AdminGalleryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/waitlist"
        element={
          <ProtectedRoute adminOnly>
            <AdminWaitlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/database-mirroring"
        element={
          <ProtectedRoute adminOnly>
            <AdminDatabaseMirroringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/storage"
        element={
          <ProtectedRoute adminOnly>
            <AdminStoragePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blogs"
        element={
          <ProtectedRoute blogsAdminOnly>
            <AdminBlogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-log"
        element={
          <ProtectedRoute superAdminOnly>
            <AdminAuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user-activity"
        element={
          <ProtectedRoute adminOnly>
            <AdminUserActivityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/errors"
        element={
          <ProtectedRoute superAdminOnly>
            <AdminErrorsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/emails"
        element={
          <ProtectedRoute adminOnly>
            <AdminEmailsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Protected Mentor Routes */}
      <Route
        path="/mentor"
        element={
          <ProtectedRoute mentorOnly>
            <MentorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mentor/reviews"
        element={
          <ProtectedRoute mentorOnly>
            <MentorReviewsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mentor/review/:submissionId"
        element={
          <ProtectedRoute mentorOnly>
            <MentorReviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mentor/delegates"
        element={
          <ProtectedRoute mentorOnly>
            <MentorDelegatesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </Suspense>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0D1B2A',
              color: '#fff',
              border: 'none',
            },
            className: 'elevate-toast',
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
